import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  fetchToolCommunityMetadata,
  fetchWorkflowCommunityMetadata,
  type CommunityMetadataBase,
  type PublishToolResponse,
  type PublishWorkflowResponse,
  type ToolCommunityMetadata,
  type WorkflowCommunityMetadata,
} from '@/api/workflowCommunityPublishing.api';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useLinkedFigmaAccount } from '@/hooks/useLinkedFigmaAccount';
import { FF_TOOL_COMMUNITY_PUBLISHING, FF_WORKFLOW_COMMUNITY_PUBLISHING } from '@/consts/featureFlags';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES } from '@/consts/analytics.consts';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { isLiveAndVerified, isPendingReview } from './workflowCommunityStatus';
import type { WorkflowCommunityPublishSurface } from './workflowCommunityPublishAnalytics';

const WORKFLOW_COMMUNITY_METADATA_QUERY_KEY = 'workflow-community-metadata';
// Deliberately a SEPARATE key from the workflow one: a tool publishes under its own `weave_app_id`,
// and sharing the workflow key previously caused an infinite refetch/render loop.
const TOOL_COMMUNITY_METADATA_QUERY_KEY = 'tool-community-metadata';

const COMMUNITY_METADATA_STALE_MS = 60_000;
const COMMUNITY_METADATA_POLL_MIN_INTERVAL_MS = 5_000;
const COMMUNITY_METADATA_POLL_MAX_INTERVAL_MS = 30_000;
const COMMUNITY_METADATA_POLL_BACKOFF_STEP_MS = 30_000;
// The scan is a Temporal workflow with a 30-minute execution timeout; giving up sooner leaves the
// banner stuck on "under review" until a manual refresh.
const COMMUNITY_METADATA_POLL_TIMEOUT_MS = 30 * 60_000;

/** Tight while the verdict is most likely to land, doubling every 30s up to one poll per 30s. */
const communityMetadataPollInterval = (elapsedMs: number) =>
  Math.min(
    COMMUNITY_METADATA_POLL_MAX_INTERVAL_MS,
    COMMUNITY_METADATA_POLL_MIN_INTERVAL_MS * 2 ** Math.floor(elapsedMs / COMMUNITY_METADATA_POLL_BACKOFF_STEP_MS),
  );

const communityMetadataQueryKey = (queryKeyPrefix: string, id: string | undefined) => [queryKeyPrefix, id] as const;

/**
 * One resource's wait for a moderation verdict.
 *
 * `endedAtMs` is stamped by the first instance to see the verdict, whether or not that instance is
 * the one entitled to report it, so the reported latency is when the verdict was *observed* rather
 * than whenever some effect happened to run — and so the next wait for this resource knows to start
 * fresh instead of reusing a wait that is already over.
 */
type PendingPublishWatch = {
  startedAtMs: number;
  observedFromPublish: boolean;
  endedAtMs: number | null;
  reported: boolean;
};

const newPendingPublishWatch = (observedFromPublish: boolean): PendingPublishWatch => ({
  startedAtMs: Date.now(),
  observedFromPublish,
  endedAtMs: null,
  reported: false,
});

/**
 * The waits currently being timed, keyed by `prefix:id`, scoped to the `QueryClient` that holds the
 * metadata they describe.
 *
 * Shared rather than per-hook because this hook is mounted many times over at once — the editor
 * banner, the share panel, and the publish modal the panel keeps mounted even when closed all watch
 * the same resource. Per-instance state would have each of them report the same verdict separately.
 *
 * An entry is written either by the seed helpers below, which run on the publish response, so the
 * wait can be measured from the click (`observedFromPublish: true`), or on the first observation of
 * a resource that was already pending when the page opened. An entry alone does not make an instance
 * eligible to report it — entries outlive the mount that opened them, so eligibility is per mount.
 */
const pendingPublishWatches = new WeakMap<QueryClient, Map<string, PendingPublishWatch>>();

const pendingPublishWatchesFor = (queryClient: QueryClient) => {
  const existing = pendingPublishWatches.get(queryClient);
  if (existing) return existing;
  const created = new Map<string, PendingPublishWatch>();
  pendingPublishWatches.set(queryClient, created);
  return created;
};

const pendingPublishWatchKey = (queryKeyPrefix: string, id: string) => `${queryKeyPrefix}:${id}`;

const startPendingPublishWatch = (queryClient: QueryClient, queryKeyPrefix: string, id: string) =>
  pendingPublishWatchesFor(queryClient).set(pendingPublishWatchKey(queryKeyPrefix, id), newPendingPublishWatch(true));

/**
 * Shared implementation for the workflow and tool community-metadata queries. They are identical
 * except for the query key, the fetch function, and the feature flag that gates them.
 */
const useCommunityMetadata = <T extends CommunityMetadataBase>(
  id: string | undefined,
  queryKeyPrefix: string,
  fetchMetadata: (id: string) => Promise<T | null>,
  featureFlag: string,
  surface: WorkflowCommunityPublishSurface,
) => {
  const { value: isFeatureFlagEnabled } = useFeatureFlag(featureFlag);
  const { data: figmaConnection } = useLinkedFigmaAccount();
  const isFigmaLinked = figmaConnection?.connected === true;
  const isWorkflowEditor = useUserWorkflowRole() === 'editor';
  const queryClient = useQueryClient();
  const pendingSinceRef = useRef<number | null>(null);
  const pendingVersionRef = useRef<number | null>(null);
  useEffect(() => {
    pendingSinceRef.current = null;
    pendingVersionRef.current = null;
  }, [id]);
  const query = useQuery({
    queryKey: communityMetadataQueryKey(queryKeyPrefix, id),
    queryFn: async () => {
      if (!id) throw new Error('id is required');
      const metadata = await fetchMetadata(id);
      if (metadata === null) {
        const existing = queryClient.getQueryData<T>(communityMetadataQueryKey(queryKeyPrefix, id));
        if (isPendingReview(existing)) return existing;
      }
      return metadata;
    },
    // Core reads this with the caller's own Figma grant, so an unlinked caller only ever gets a 403.
    enabled: Boolean(id) && isFeatureFlagEnabled === true && isFigmaLinked,
    // Master's 404-storm fix (PR #841258): cache terminal metadata so a remount/refocus reuses it
    // instead of refetching; pending-review metadata stays stale so it keeps polling.
    staleTime: (query) => (isPendingReview(query.state.data) ? 0 : COMMUNITY_METADATA_STALE_MS),
    refetchOnWindowFocus: isFigmaLinked && isWorkflowEditor,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!isPendingReview(data)) {
        pendingSinceRef.current = null;
        pendingVersionRef.current = null;
        return false;
      }
      const version = data?.current_version?.weavy_version_number ?? null;
      const now = Date.now();
      if (pendingSinceRef.current == null || version !== pendingVersionRef.current) {
        pendingSinceRef.current = now;
        pendingVersionRef.current = version;
      }
      const elapsed = now - pendingSinceRef.current;
      if (elapsed >= COMMUNITY_METADATA_POLL_TIMEOUT_MS) return false;
      return communityMetadataPollInterval(elapsed);
    },
  });

  useCommunityMetadataOutcomeTracking(query.data, query.dataUpdatedAt, id, queryKeyPrefix, surface, queryClient);

  return query;
};

/**
 * Reports how a publish actually ended: live, rejected, or still unresolved when the poll gave up.
 *
 * Deliberately not folded into `refetchInterval` — that callback re-runs on every scheduling pass and
 * on remount, so anything emitted from it double-counts. The outcome is derived from the data in an
 * effect instead, by whichever instance both watched the wait and consumes its shared
 * `pendingPublishWatches` entry — so the several instances mounted at once report the verdict between
 * them exactly once, and a mount that arrived after the verdict reports nothing at all.
 *
 * One blind spot, and it is not fixable from the client: a *content*-gate rejection (Weave core's own
 * scan) clears `content_verified_at` but leaves `publishing_status` alone, which reads as still
 * pending — and so does a review that simply never returns. So `REJECTED` only ever carries
 * `gate: 'listing'`, and neither of those two cases is visible from the client at all. Both are
 * server-side questions, answered by `community_publish.verdict_latency_ms` and
 * `workflow_publish_moderation.errored`.
 */
const useCommunityMetadataOutcomeTracking = <T extends CommunityMetadataBase>(
  metadata: T | null | undefined,
  dataUpdatedAt: number,
  id: string | undefined,
  queryKeyPrefix: string,
  surface: WorkflowCommunityPublishSurface,
  queryClient: QueryClient,
) => {
  const { track } = useAnalytics();
  /**
   * Whether this mount has seen the wait it is about to report still pending — its eligibility to
   * report, which is a different question from whether the outcome has already been reported. That
   * one is shared, because instances race to report; this one cannot be, because the whole point is
   * that a mount which arrived after the verdict has nothing to say about it.
   */
  const watchedWaitRef = useRef(false);
  /** When this mount started watching this resource, against which `dataUpdatedAt` is judged fresh. */
  const observingSinceRef = useRef(0);

  useEffect(() => {
    watchedWaitRef.current = false;
    observingSinceRef.current = Date.now();
  }, [id, queryKeyPrefix]);

  useEffect(() => {
    if (!id || metadata == null) return;

    const watches = pendingPublishWatchesFor(queryClient);
    const watchKey = pendingPublishWatchKey(queryKeyPrefix, id);
    const baseContext = {
      surface,
      ...(surface === 'tool' ? { toolId: id } : { workflowId: id }),
      resourceId: metadata.resource_id,
    };

    // Read as a plain boolean: `isPendingReview` is a type guard, so branching on it directly narrows
    // the terminal branch below to `never`.
    const isPending: boolean = isPendingReview(metadata);
    if (isPending) {
      // Just remember when the wait started, so the verdict below can be timed. A review that never
      // returns is deliberately not reported from here: the server sees that case completely, through
      // `community_publish.verdict_latency_ms` and `workflow_publish_moderation.errored`, and a timer
      // in a polling hook is a lot of moving parts for a worse view of it.
      const inFlight = watches.get(watchKey);
      // A wait that has already ended is not this one: reusing it would time this review from the
      // previous publish. That happens when the verdict landed while nobody was mounted and the
      // republish came from somewhere other than this client's own publish, which reseeds outright.
      if (inFlight == null || inFlight.endedAtMs != null) watches.set(watchKey, newPendingPublishWatch(false));

      // Arm this mount only on a *fetched* pending, never on cache. Returning to a workflow inside
      // `gcTime` serves the pending data cached before the absence, and treating that as having
      // watched the review would let the refetch's verdict be reported with the absence folded into
      // its latency. A refetch that comes back still pending arms it properly a moment later.
      if (dataUpdatedAt >= observingSinceRef.current) watchedWaitRef.current = true;
      return;
    }

    // Terminal. Whoever gets here first closes the wait, reporter or not, so the latency is measured
    // to the verdict's first observation and the next wait for this resource starts clean.
    const watch = watches.get(watchKey);
    if (watch == null) return;
    watch.endedAtMs ??= Date.now();

    // An unpublish is the owner's own doing, not an outcome of moderation.
    if (metadata.unpublished_at) return;

    // Only report a verdict this mount watched arrive. A watch outlives the mount that opened it, so
    // without this an editor who saw the listing pending, left, and came back once it had long since
    // gone live would report a `msSincePublish` that is mostly their own absence.
    if (!watchedWaitRef.current) return;
    watchedWaitRef.current = false;

    // And only once between them all — banner, panel and modal each mount this hook for the same
    // resource, and each would otherwise report the verdict separately.
    if (watch.reported) return;
    watch.reported = true;

    // True only when this client watched the publish itself. Otherwise the wait is measured from the
    // first time the page saw the resource pending, which undercounts — the review had already begun.
    const { observedFromPublish } = watch;
    const msSincePublish = watch.endedAtMs - watch.startedAtMs;

    if (isLiveAndVerified(metadata)) {
      track(
        WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.LIVE,
        { ...baseContext, msSincePublish, observedFromPublish },
        TrackTypeEnum.BI,
      );
      return;
    }

    track(
      WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.REJECTED,
      { ...baseContext, gate: 'listing', publishingStatus: metadata.publishing_status, msSincePublish },
      TrackTypeEnum.Product,
    );
    // `dataUpdatedAt` is a dependency in its own right: a poll that comes back unchanged is
    // structurally shared, so `metadata` keeps its identity and only the timestamp moves.
  }, [dataUpdatedAt, id, metadata, queryClient, queryKeyPrefix, surface, track]);
};

export const useWorkflowCommunityMetadata = (weaveWorkflowId: string | undefined) =>
  useCommunityMetadata<WorkflowCommunityMetadata>(
    weaveWorkflowId,
    WORKFLOW_COMMUNITY_METADATA_QUERY_KEY,
    fetchWorkflowCommunityMetadata,
    FF_WORKFLOW_COMMUNITY_PUBLISHING,
    'workflow',
  );

export const useToolCommunityMetadata = (weaveAppId: string | undefined) =>
  useCommunityMetadata<ToolCommunityMetadata>(
    weaveAppId,
    TOOL_COMMUNITY_METADATA_QUERY_KEY,
    fetchToolCommunityMetadata,
    FF_TOOL_COMMUNITY_PUBLISHING,
    'tool',
  );

export const invalidateWorkflowCommunityMetadata = (queryClient: QueryClient, weaveWorkflowId: string) =>
  queryClient.invalidateQueries({
    queryKey: communityMetadataQueryKey(WORKFLOW_COMMUNITY_METADATA_QUERY_KEY, weaveWorkflowId),
  });

export const invalidateToolCommunityMetadata = (queryClient: QueryClient, weaveAppId: string) =>
  queryClient.invalidateQueries({
    queryKey: communityMetadataQueryKey(TOOL_COMMUNITY_METADATA_QUERY_KEY, weaveAppId),
  });

export const seedPendingWorkflowCommunityMetadata = (
  queryClient: QueryClient,
  weaveWorkflowId: string,
  response: PublishWorkflowResponse,
) => {
  startPendingPublishWatch(queryClient, WORKFLOW_COMMUNITY_METADATA_QUERY_KEY, weaveWorkflowId);
  return queryClient.setQueryData<WorkflowCommunityMetadata>(
    communityMetadataQueryKey(WORKFLOW_COMMUNITY_METADATA_QUERY_KEY, weaveWorkflowId),
    {
      resource_id: response.resource_id,
      publishing_status: 'pending_public',
      community_url: null,
      unpublished_at: null,
      current_version: { id: response.version_id, weavy_version_number: response.weavy_version_number },
      content_verified_at: null,
      weave_workflow_id: weaveWorkflowId,
    },
  );
};

export const seedPendingToolCommunityMetadata = (
  queryClient: QueryClient,
  weaveAppId: string,
  response: PublishToolResponse,
) => {
  startPendingPublishWatch(queryClient, TOOL_COMMUNITY_METADATA_QUERY_KEY, weaveAppId);
  return queryClient.setQueryData<ToolCommunityMetadata>(
    communityMetadataQueryKey(TOOL_COMMUNITY_METADATA_QUERY_KEY, weaveAppId),
    {
      resource_id: response.resource_id,
      publishing_status: 'pending_public',
      community_url: null,
      unpublished_at: null,
      current_version: { id: response.version_id, weavy_version_number: response.weavy_version_number },
      content_verified_at: null,
      weave_app_id: weaveAppId,
    },
  );
};
